import React, { useState } from 'react';
import { bool, node, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';

import { Form, PrimaryButton, FieldTextInput, CustomExtendedDataField } from '../../../components';

import FieldSelectUserType from '../FieldSelectUserType';
import UserFieldDisplayName from '../UserFieldDisplayName';
import UserFieldPhoneNumber from '../UserFieldPhoneNumber';
import PhoneInput from 'react-phone-input-2'

import css from './ConfirmSignupForm.module.css';

const normalizeMsisdn = raw => {
  if (!raw) return null;
  let s = String(raw).trim().replace(/\s+/g, '');
  if (s.startsWith('+')) s = s.slice(1);
  if (s.startsWith('00966')) s = s.replace(/^00966/, '966');
  if (s.startsWith('966')) return s;
  if (s.startsWith('05')) return '966' + s.slice(1);
  if (s.startsWith('5')) return '966' + s;
  return s;
};

const isValidKsaMsisdnInput = rawValue => {
  if (!rawValue) return false;
  const norm = normalizeMsisdn(String(rawValue));
  if (!norm) return false;
  return /^9665\d{8}$/.test(norm) || /^96605\d{8}$/.test(norm);
};

const getSoleUserTypeMaybe = userTypes =>
  Array.isArray(userTypes) && userTypes.length === 1 ? userTypes[0].userType : null;

const ConfirmSignupFormComponent = props => {
  if (typeof window === 'undefined') return null;
  require('../../../styles/react-phone-input-2.css');
  const [phoneTouched, setPhoneTouched] = useState(false);

  return <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    initialValues={{ userType: props.preselectedUserType || getSoleUserTypeMaybe(props.userTypes) }}
    render={formRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        invalid,
        intl,
        form,
        termsAndConditions,
        authInfo,
        idp,
        preselectedUserType,
        userTypes,
        userFields,
        values,
        submitSucceeded,
      } = formRenderProps;

      const { userType, mobile_phone_number } = values || {};

      // email
      const emailRequired = validators.required(
        intl.formatMessage({
          id: 'ConfirmSignupForm.emailRequired',
        })
      );
      const emailValid = validators.emailFormatValid(
        intl.formatMessage({
          id: 'ConfirmSignupForm.emailInvalid',
        })
      );

      // Custom user fields - filter to only show gender and dateOfBirth in IDP signup
      const allUserFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);
      const userFieldProps = allUserFieldProps.filter(field => 
        field.key === 'prot_gender' || field.key === 'prot_dateOfBirth'
      );

      const noUserTypes = !userType && !(userTypes?.length > 0);
      const userTypeConfig = userTypes.find(config => config.userType === userType);
      const showDefaultUserFields = userType || noUserTypes;
      const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;

      const phoneRequired = true;
      const hasPhone = !!mobile_phone_number?.phoneNumber;
      const isValidPhoneNumber = hasPhone
        ? isValidKsaMsisdnInput(mobile_phone_number?.value)
        : false;

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const showPhoneErrors = phoneRequired && !submitInProgress && !submitSucceeded && phoneTouched;
      const submitDisabled =
        invalid ||
        submitInProgress ||
        (phoneRequired && (!hasPhone || !isValidPhoneNumber));

      // If authInfo is not available we should not show the ConfirmForm
      if (!authInfo) {
        return;
      }

      // Initial values from idp provider
      const { email, firstName, lastName } = authInfo;

      const handleChangePhoneInput = (value, country) => {
        const { dialCode = '', countryCode = '' } = country;
        const normalizedValue = normalizeMsisdn(value) || value;
        const normalizedWithoutDialCode =
          dialCode && String(normalizedValue).startsWith(String(dialCode))
            ? String(normalizedValue).slice(String(dialCode).length)
            : String(normalizedValue);
        form.change('phoneNumber', '+' + normalizedValue);
        const newState = {
          countryCode: countryCode?.toUpperCase(),
          callingCode: dialCode,
          phoneNumber: normalizedWithoutDialCode,
          value: normalizedValue,
        }
        form.change('mobile_phone_number', newState);
      };

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <FieldSelectUserType
            name="userType"
            userTypes={userTypes}
            hasExistingUserType={!!preselectedUserType}
            intl={intl}
          />

          {showDefaultUserFields ? (
            <div className={css.defaultUserFields}>
              <FieldTextInput
                type="email"
                id={formId ? `${formId}.email` : 'email'}
                name="email"
                autoComplete="email"
                label={intl.formatMessage({
                  id: 'ConfirmSignupForm.emailLabel',
                })}
                placeholder={intl.formatMessage({
                  id: 'ConfirmSignupForm.emailPlaceholder',
                })}
                initialValue={email}
                validate={validators.composeValidators(emailRequired, emailValid)}
                disabled={true}
              />
              <div className={css.name}>
                <FieldTextInput
                  className={css.firstNameRoot}
                  type="text"
                  id={formId ? `${formId}.firstName` : 'firstName'}
                  name="firstName"
                  autoComplete="given-name"
                  label={intl.formatMessage({
                    id: 'ConfirmSignupForm.firstNameLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'ConfirmSignupForm.firstNamePlaceholder',
                  })}
                  initialValue={firstName}
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'ConfirmSignupForm.firstNameRequired',
                    })
                  )}
                />
                <FieldTextInput
                  className={css.lastNameRoot}
                  type="text"
                  id={formId ? `${formId}.lastName` : 'lastName'}
                  name="lastName"
                  autoComplete="family-name"
                  label={intl.formatMessage({
                    id: 'ConfirmSignupForm.lastNameLabel',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'ConfirmSignupForm.lastNamePlaceholder',
                  })}
                  initialValue={lastName}
                  validate={validators.required(
                    intl.formatMessage({
                      id: 'ConfirmSignupForm.lastNameRequired',
                    })
                  )}
                />
              </div>

              <div className={css.phoneInput}>
                <PhoneInput
                  name="phoneNumber"
                  id={formId ? `${formId}.phoneNumber` : 'phoneNumber'}
                  country={'sa'}
                  onlyCountries={['sa']}
                  disableDropdown={true}
                  value={mobile_phone_number?.callingCode && mobile_phone_number?.phoneNumber
                    ? '+' + mobile_phone_number.callingCode + mobile_phone_number.phoneNumber
                    : ''}
                  placeholder={intl.formatMessage({
                    id: 'SignupForm.phoneNumberLabel',
                  })}
                  onChange={handleChangePhoneInput}
                  onBlur={() => setPhoneTouched(true)}
                />
              </div>
              {showPhoneErrors && !hasPhone ? (
                <div className={css.errorText}>
                  <FormattedMessage id="SignupForm.phoneNumberRequired" />
                </div>
              ) : null}
              {showPhoneErrors && hasPhone && !isValidPhoneNumber ? (
                <div className={css.errorText}>
                  <FormattedMessage id="EditListingDetailsForm.phoneNumberInvalid" />
                </div>
              ) : null}

              {/* <UserFieldDisplayName
                formName="ConfirmSignupForm"
                className={css.row}
                userTypeConfig={userTypeConfig}
                intl={intl}
              />
              <UserFieldPhoneNumber
                formName="ConfirmSignupForm"
                className={css.row}
                userTypeConfig={userTypeConfig}
                intl={intl}
              /> */}
            </div>
          ) : null}

          {/* Gender and DOB fields for both customers and merchants */}
          {showCustomUserFields ? (
            <div className={css.customFields}>
              {userFieldProps.map(fieldProps => (
                <CustomExtendedDataField key={fieldProps.key} {...fieldProps} formId={formId} hideLabel={true} />
              ))}
            </div>
          ) : null}

          {/* Business name field for providers only */}
          {showDefaultUserFields === 'provider' ? (
            <div className={css.inputBoxBusiness}>
              <FieldTextInput
                type="text"
                className={css.inputBox}
                id={formId ? `${formId}.business_name` : 'business_name'}
                name="business_name"
                autoComplete="business_name"
                label={intl.formatMessage({ id: 'SignupForm.businessNameLabel' })}
                placeholder={intl.formatMessage({ id: 'SignupForm.businessNamePlaceholder' })}
                validate={validators.required(
                  intl.formatMessage({ id: 'SignupForm.businessNameRequired' })
                )}
              />
            </div>
          ) : null}

          <div className={css.bottomWrapper}>
            {termsAndConditions}
            <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
              <FormattedMessage id="ConfirmSignupForm.signUp" values={{ idp: idp }} />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
};

ConfirmSignupFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  formId: null,
  inProgress: false,
  preselectedUserType: null,
};

ConfirmSignupFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  formId: string,
  inProgress: bool,
  termsAndConditions: node.isRequired,
  preselectedUserType: string,
  userTypes: propTypes.userTypes.isRequired,
  userFields: propTypes.listingFields.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const ConfirmSignupForm = compose(injectIntl)(ConfirmSignupFormComponent);
ConfirmSignupForm.displayName = 'ConfirmSignupForm';

export default ConfirmSignupForm;
